/* eslint-disable max-lines-per-function */
import HelloSign from "hellosign-embedded";
import React from "react";
import { trackEvent } from "./analytics";

type Opts = {
  testMode: boolean;
  clientId: string;
  metadata?: pendo.Metadata;
}

export const useHelloSign = (
  opts: Opts,
): [(iframeUrl: string
  ) => Promise<{ signatureId: string }>] => {
  const client = React.useMemo(
    () =>
    // @ts-ignore
      new HelloSign({ clientId: opts.clientId })
    , [],
  );

  const openModal = (url: string) => {
    client.off("sign");
    client.off("error");
    client.off("cancel");
    client.off("close");
    client.off("finish");

    return new Promise<{ signatureId: string }>((resolve, reject) => {
      let settled = false;
      let handleFallbackCloseClick = (_event: MouseEvent) => {};
      const clearListeners = () => {
        client.off("sign");
        client.off("error");
        client.off("cancel");
        client.off("close");
        client.off("finish");
        document.removeEventListener("click", handleFallbackCloseClick, true);
      };
      const resolveOnce = (value: { signatureId: string }) => {
        if (settled) return;
        settled = true;
        clearListeners();
        trackEvent("user:sign_complete", opts.metadata);
        resolve(value);
      };
      const rejectOnce = (error: Error, eventName: "user:sign_failed" | "user:sign_cancelled") => {
        if (settled) return;
        settled = true;
        clearListeners();
        trackEvent(eventName, opts.metadata);
        reject(error);
      };
      handleFallbackCloseClick = (event: MouseEvent) => {
        const { target } = event;
        if (!(target instanceof Element)) return;
        if (!target.closest(".x-hellosign-embedded__modal-close-button")) return;
        try {
          client.close();
        } catch {
          // best-effort close
        }
        rejectOnce(new Error("Signature modal closed before completion"), "user:sign_cancelled");
      };

      client.on("sign", ({ signatureId }) => {
        resolveOnce({ signatureId: signatureId || "" });
      });
      client.on("error", () => {
        rejectOnce(new Error("Signature error"), "user:sign_failed");
      });
      client.on("cancel", () => {
        rejectOnce(new Error("Signature modal closed before completion"), "user:sign_cancelled");
      });
      client.on("close", () => {
        // Do not reject on close: it fires when the window closes (after success or cancel).
        // Success is handled by "sign"/"finish"; cancel is handled by "cancel".
      });
      client.on("finish", () => {
        resolveOnce({ signatureId: "" });
      });
      document.addEventListener("click", handleFallbackCloseClick, true);
      try {
        client.open(url, {
          testMode: opts.testMode,
          allowCancel: true,
        });
      } catch {
        rejectOnce(new Error("Error opening signature modal"), "user:sign_failed");
      }
    });
  };

  return [openModal];
};
